import { createApi } from '@reduxjs/toolkit/query/react';
import camelize from 'camelize';
import {
  ActivitiesResponse,
  Activity,
  CreateActivityParams,
  DeleteActivityParams,
  DispensaryContact,
  ExportActivitiesParams,
  GetActivitiesParams,
  GetActivityParams,
  GetContactsParams,
  GetVendorUsersParams,
  UpdateActivityParams,
  VendorUser,
} from '../../dashboard/components/VendorCRM/VendorCustomerDetails/Activities/types';
import { createMutationHandler } from '../utils/errorHandling';
import { baseQueryWithToast } from './base';

export const customerActivitiesApi = createApi({
  baseQuery: baseQueryWithToast,
  endpoints: (builder) => ({
    getActivities: builder.query<ActivitiesResponse, GetActivitiesParams>({
      query: ({
        vendorId,
        customerId,
        page = 1,
        pageSize = 10,
        search = '',
        activityType,
        activityStatus,
        userId,
        startDate,
        endDate,
      }) => {
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('page_size', String(pageSize));

        if (search) {
          params.append('title', search);
        }

        if (activityType) {
          const types = Array.isArray(activityType)
            ? activityType
            : [activityType];
          types.forEach((type) => params.append('activity_type', type));
        }

        if (activityStatus) {
          const statuses = Array.isArray(activityStatus)
            ? activityStatus
            : [activityStatus];
          statuses.forEach((status) =>
            params.append('activity_status', status)
          );
        }

        if (userId) {
          const userIds = Array.isArray(userId) ? userId : [userId];
          userIds.forEach((id) => params.append('user_id', String(id)));
        }

        if (startDate) {
          params.append('from_date', startDate);
        }

        if (endDate) {
          params.append('to_date', endDate);
        }

        return {
          url: `/api/vendor/${vendorId}/customer/${customerId}/activities/?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['CustomerActivities'],
      transformResponse: (response: any) => {
        const camelizedItems = camelize(response.items);
        return {
          activities: camelizedItems,
          filterFormData: {
            currentPage: response.pagination.number,
            totalCount: response.pagination.count,
            totalPageCount: response.pagination.pages,
          },
        };
      },
    }),

    getActivity: builder.query<Activity, GetActivityParams>({
      query: ({ vendorId, customerId, id }) => ({
        url: `/api/vendor/${vendorId}/customer/${customerId}/activities/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: 'CustomerActivities', id },
      ],
      transformResponse: (response: any) => camelize(response.activity),
    }),

    createActivity: builder.mutation<
      { activity: Activity },
      CreateActivityParams
    >({
      query: ({ vendorId, customerId, activity }) => ({
        url: `/api/vendor/${vendorId}/customer/${customerId}/activities/`,
        method: 'POST',
        body: {
          activity: {
            title: activity.title,
            description: activity.description,
            activity_type: activity.activityType,
            activity_status: activity.activityStatus,
            due_at: activity.dueAt,
            contact_id: activity.contactId,
            contact_type: activity.contactType,
          },
        },
      }),
      invalidatesTags: ['CustomerActivities'],
      onQueryStarted: createMutationHandler(
        'Failed to create activity',
        'Activity created successfully'
      ),
      transformResponse: camelize,
    }),

    updateActivity: builder.mutation<
      { activity: Activity },
      UpdateActivityParams
    >({
      query: ({ vendorId, customerId, id, activity }) => ({
        url: `/api/vendor/${vendorId}/customer/${customerId}/activities/${id}/`,
        method: 'PUT',
        body: {
          activity: {
            title: activity.title,
            description: activity.description,
            activity_type: activity.activityType,
            activity_status: activity.activityStatus,
            due_at: activity.dueAt,
            contact_id: activity.contactId,
            contact_type: activity.contactType,
          },
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        'CustomerActivities',
        { type: 'CustomerActivities', id },
      ],
      onQueryStarted: createMutationHandler(
        'Failed to update activity',
        'Activity updated successfully'
      ),
      transformResponse: camelize,
    }),

    deleteActivity: builder.mutation<void, DeleteActivityParams>({
      query: ({ vendorId, customerId, id }) => ({
        url: `/api/vendor/${vendorId}/customer/${customerId}/activities/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CustomerActivities'],
      onQueryStarted: createMutationHandler(
        'Failed to delete activity',
        'Activity deleted successfully'
      ),
    }),

    exportActivities: builder.mutation<Blob, ExportActivitiesParams>({
      query: ({ vendorId, customerId, activityIds = [] }) => ({
        url: `/api/vendor/${vendorId}/customer/${customerId}/activities/export/`,
        method: 'POST',
        body: { activity_ids: activityIds },
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob;
        },
      }),
    }),

    getVendorUsers: builder.query<VendorUser[], GetVendorUsersParams>({
      query: ({ vendorId }) => ({
        url: `/api/vendor/${vendorId}/vendor-users/`,
        method: 'GET',
      }),
      providesTags: ['VendorUsers'],
      transformResponse: (response: any) => camelize(response.items),
    }),

    getContacts: builder.query<DispensaryContact[], GetContactsParams>({
      query: ({ vendorId, customerId }) => ({
        url: `/api/vendor/${vendorId}/customer/${customerId}/contacts/`,
        method: 'GET',
      }),
      providesTags: ['DispensaryContacts'],
      transformResponse: (response: any) => {
        const data = camelize(response);
        const rawList = data.contacts ?? data.items ?? [];
        const activityContactType: Record<
          string,
          DispensaryContact['contactType']
        > = {
          dispensary_user: 'DispensaryUser',
          vendor_contact: 'VendorDispensaryContact',
        };
        return rawList.map((c: any) => {
          const mappedType =
            activityContactType[c.contactType] ?? c.contactType;
          const nameFromParts = [c.firstName, c.lastName]
            .filter(Boolean)
            .join(' ')
            .trim();
          return {
            id: c.id,
            contactType: mappedType,
            name: nameFromParts || c.email || '',
            email: c.email,
            phone: c.phone,
          };
        });
      },
    }),
  }),
  reducerPath: 'customerActivities',
  tagTypes: ['CustomerActivities', 'VendorUsers', 'DispensaryContacts'],
});

export const {
  useGetActivitiesQuery,
  useGetActivityQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useExportActivitiesMutation,
  useGetVendorUsersQuery,
  useGetContactsQuery,
} = customerActivitiesApi;
